import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { applicationBuilderAPI } from '../../api/applicationBuilderAPI';
import './EditorStyles.css';
import './CoverLetterEditorStyles.css';

const CoverLetterEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [documentData, setDocumentData] = useState(location.state?.documentData || null);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [content, setContent] = useState({});
  const [graphFeedback, setGraphFeedback] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);

  useEffect(() => {
    if (!documentData) {
      navigate('/application-builder/form/cover_letter');
    } else {
      setContent(documentData.cover_letter_content || {});
    }
  }, [documentData, navigate]);

  const handleGraphFeedback = async () => {
    setGraphLoading(true);
    try {
      const body = [
        content.opening,
        content.why_firm,
        content.why_me,
        content.technical_bridge,
        content.closing,
        content.body,
        documentData?.cover_letter_content,
        documentData?.ai_content,
      ].filter(Boolean).join('\n\n');
      const response = await applicationBuilderAPI.getGraphFeedback({
        elementId: 'cl',
        content: body,
        document: { ...documentData, cover_letter_content: content },
      });
      setGraphFeedback(response);
    } catch (error) {
      console.error('CL graph feedback error:', error);
      alert('Failed to get graph feedback. Please try again.');
    } finally {
      setGraphLoading(false);
    }
  };

  const handleDownload = () => {
    if (!documentData?.cl_docx) return;
    setLoading(true);
    applicationBuilderAPI.openBuilderDocument(documentData.cl_docx)
      .catch((error) => {
        console.error('Error downloading cover letter:', error);
        alert(error?.message === 'popup'
          ? 'Allow pop-ups for this site to open documents.'
          : 'Failed to download cover letter. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const handleFieldEdit = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
    // Optionally save to backend
    // applicationBuilderAPI.updateContent(field, value);
  };

  const openAIModal = (field) => {
    setEditingField(field);
    setAiModalOpen(true);
    setAiPrompt('');
    setAiSuggestion(null);
  };

  const closeAIModal = () => {
    setAiModalOpen(false);
    setEditingField(null);
    setAiPrompt('');
    setAiSuggestion(null);
  };

  const getAISuggestion = async () => {
    if (!editingField) return;
    
    setAiLoading(true);
    try {
      const currentText = content[editingField] || '';
      const response = await applicationBuilderAPI.improveText(
        currentText,
        editingField,
        aiPrompt.trim() || 'Make it more impactful'
      );
      
      if (response.improved_text) {
        setAiSuggestion(response.improved_text);
      }
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      alert('Failed to get AI suggestion. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const acceptSuggestion = () => {
    if (aiSuggestion && editingField) {
      handleFieldEdit(editingField, aiSuggestion);
      closeAIModal();
    }
  };

  const EditableSection = ({ field, label, children, allowAI = true, multiline = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(content[field] || '');

    const handleSave = () => {
      handleFieldEdit(field, editValue);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(content[field] || '');
      setIsEditing(false);
    };

    return (
      <div className="editable-section">
        <div className="editable-section-header">
          <label>{label}</label>
          <div className="editable-section-controls">
            {!isEditing && (
              <>
                <button
                  type="button"
                  className="control-btn edit-btn"
                  onClick={() => setIsEditing(true)}
                >
                  EDIT
                </button>
                {allowAI && (
                  <button
                    type="button"
                    className="control-btn ai-btn"
                    onClick={() => openAIModal(field)}
                  >
                    AI
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="editing-container">
            {multiline ? (
              <textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="editable-textarea"
                rows={4}
              />
            ) : (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="editable-input"
              />
            )}
            <div className="editing-actions">
              <button type="button" className="save-btn" onClick={handleSave}>Done</button>
              <button type="button" className="cancel-btn" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="editable-content-display">
            {children || (content[field] || '')}
          </div>
        )}
      </div>
    );
  };

  if (!documentData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="editor-container cl-editor-container">
      <div className="editor-wrapper">
        <div className="editor-header">
          <h1>Cover Letter Editor</h1>
          <p>Review and edit your cover letter. Use EDIT to change text and AI to improve it.</p>
        </div>

        <div className="editor-actions">
          <button type="button" onClick={() => navigate('/application-builder/form/cover_letter')} className="edit-button">
            Edit Form
          </button>
          <button type="button" onClick={handleGraphFeedback} className="edit-button" disabled={graphLoading}>
            {graphLoading ? 'Scoring…' : 'Get Graph Feedback'}
          </button>
          <button type="button" onClick={handleDownload} className="download-button" disabled={loading}>
            {loading ? 'Downloading...' : 'Download Cover Letter'}
          </button>
        </div>

        {graphFeedback?.feedback && (
          <div className="ps-feedback-box" style={{ margin: '1rem 0', padding: '1rem', border: '1px solid var(--iv-line)', borderRadius: 8 }}>
            <div className="ps-feedback-label">
              Graph feedback
              {graphFeedback.isomorphism?.band ? ` · ${graphFeedback.isomorphism.band}` : ''}
              {graphFeedback.db_write?.status ? ` · saved: ${graphFeedback.db_write.status}` : ''}
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{graphFeedback.feedback}</pre>
          </div>
        )}

        <div className="document-preview">
          <div className="preview-content cover-letter">
            {/* Sender Info */}
            <div className="sender-info">
              <EditableSection field="sender_name" label="Your Name">
                <p>{content.sender_name}</p>
              </EditableSection>
              <EditableSection field="sender_address" label="Address">
                <p>{content.sender_address}</p>
              </EditableSection>
              <EditableSection field="sender_phone" label="Phone">
                <p>{content.sender_phone}</p>
              </EditableSection>
              <EditableSection field="sender_email" label="Email">
                <p>{content.sender_email}</p>
              </EditableSection>
            </div>
            
            {/* Date */}
            <div className="date">
              <EditableSection field="date" label="Date">
                {content.date}
              </EditableSection>
            </div>
            
            {/* Recipient Info */}
            <div className="recipient-info">
              <EditableSection field="company_name" label="Company Name">
                <p>{content.company_name}</p>
              </EditableSection>
              {content.company_address && (
                <EditableSection field="company_address" label="Company Address">
                  <p>{content.company_address}</p>
                </EditableSection>
              )}
            </div>
            
            {/* Salutation */}
            <p className="salutation">Dear Sir/Madam,</p>
            
            {/* Letter Body - Paragraphs with AI */}
            <div className="letter-body">
              <EditableSection 
                field="opening_paragraph" 
                label="Opening Paragraph"
                allowAI={true}
                multiline={true}
              >
                <p>{content.opening_paragraph}</p>
              </EditableSection>
              
              <EditableSection 
                field="why_firm_paragraph" 
                label="Why This Company"
                allowAI={true}
                multiline={true}
              >
                <p>{content.why_firm_paragraph}</p>
              </EditableSection>
              
              <EditableSection 
                field="why_me_paragraph" 
                label="Why Me"
                allowAI={true}
                multiline={true}
              >
                <p>{content.why_me_paragraph}</p>
              </EditableSection>
              
              <EditableSection 
                field="technical_bridge_paragraph" 
                label="Technical Bridge"
                allowAI={true}
                multiline={true}
              >
                <p>{content.technical_bridge_paragraph}</p>
              </EditableSection>
              
              <EditableSection 
                field="closing_paragraph" 
                label="Closing Paragraph"
                allowAI={true}
                multiline={true}
              >
                <p>{content.closing_paragraph}</p>
              </EditableSection>
            </div>
            
            {/* Signature */}
            <p className="signature">Yours faithfully,</p>
            <div className="signature-name">
              <EditableSection field="signature" label="Signature">
                {content.signature}
              </EditableSection>
            </div>
          </div>
        </div>
      </div>

      {/* AI Writing Assistant modal (same as CV) */}
      {aiModalOpen && (
        <div className="modal-overlay" onClick={closeAIModal}>
          <div className="modal-content ai-assistant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✨ AI Writing Assistant</h3>
              <button type="button" className="close-btn" onClick={closeAIModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="current-text">
                <div className="text-label">CURRENT TEXT:</div>
                <div className="text-preview ai-current-text">{content[editingField] || 'No content'}</div>
              </div>
              <div className="ai-input">
                <label className="ai-prompt-label">How would you like to improve this section? (e.g., &apos;Make it more impactful&apos;, &apos;Add specific metrics&apos;, &apos;Make it more concise&apos;)</label>
                <input
                  type="text"
                  className="ai-prompt-input"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Make it more impactful"
                />
                <button type="button" className="btn btn-primary get-ai-btn" onClick={getAISuggestion} disabled={aiLoading}>
                  {aiLoading ? 'Getting suggestion...' : 'Get AI Suggestion'}
                </button>
              </div>
              {aiSuggestion && (
                <div className="ai-suggestion">
                  <div className="text-label">AI SUGGESTION:</div>
                  <div className="text-preview suggested ai-suggestion-box">{aiSuggestion}</div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeAIModal}>Keep Original</button>
                    <button type="button" className="btn btn-primary" onClick={acceptSuggestion}>Accept Suggestion</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverLetterEditor;
